import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { teamDashboardApi } from '@/api/team-dashboard';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { ROBOT_STATUS_LABELS } from '@bhops/shared';
import { Icon } from '@/components/Icon';
import { ManufacturingCalendar } from '@/components/ManufacturingCalendar';
import { ROBOT_STATUS_TINT } from '@/components/StatusPill';
import {
  AutoRefreshHint,
  DashboardError,
  DashboardSkeleton,
  HorizontalBars,
  KpiCard,
  MonthlyTrendBars,
  SHARED_CARD,
  SHARED_CARD_TITLE,
  SHARED_EMPTY_HINT,
  SHARED_REFETCH_INTERVAL_MS,
  Section,
} from './_shared';

// 로봇 상태 이름표·색 — **정본을 쓴다**(로봇 목록 뱃지·띠와 같은 글자·같은 색).
//
// ⚠ 2026-09-23 — 종전엔 이 파일만 따로 적은 표였다. 그래서 같은 상태를 두고 여기는
//   '배치 완료'·'AS 중'·'재고 보관', 로봇 목록은 '운영중'·'정비'·'재고' 였고, 폐기
//   완료(RETIRED)를 **'회수'** 로 적어 뜻이 틀려 있었다. 설치 대기·폐기 예정·기타는
//   아예 없어서 영문 코드가 그대로 떴다. 표가 `Record<string>` 이라 타입 검사도 못 잡았다.
const ROBOT_STATUS_LABEL: Record<string, string> = ROBOT_STATUS_LABELS;
const ROBOT_STATUS_COLOR: Record<string, string> = Object.fromEntries(
  Object.entries(ROBOT_STATUS_TINT).map(([k, v]) => [k, v.color]),
);
const PLAN_STATUS_LABEL: Record<string, string> = {
  PLANNED: '계획',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완성',
  PARTIALLY_COMPLETED: '부분 완성',
  DELAYED: '지연',
  CANCELLED: '취소',
};
const PLAN_STATUS_COLOR: Record<string, string> = {
  PLANNED: 'var(--warm-400)',
  IN_PROGRESS: 'var(--report-new)',
  COMPLETED: 'var(--robot-operating)',
  PARTIALLY_COMPLETED: 'var(--dday-warning)',
  DELAYED: 'var(--severity-urgent)',
  CANCELLED: 'var(--fg-disabled)',
};

export function ManufacturingDashboardPage() {
  const q = useQuery({
    queryKey: ['dashboard', 'manufacturing'],
    queryFn: () => teamDashboardApi.manufacturing(),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // S2 Phase 2.10 (시트1) — 월별 생산/출고 + 모델별 비중 KPI. 별도 쿼리로
  // 분리해 기존 manufacturing 대시보드 payload 를 건드리지 않음.
  const kpi = useQuery({
    queryKey: ['dashboard', 'manufacturing-kpi'],
    queryFn: () => teamDashboardApi.manufacturingKpi(12),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  if (q.isLoading) return <DashboardSkeleton kpiCount={3} />;
  if (q.error) return <DashboardError onRetry={q.refetch} />;
  const d = q.data!;

  const pendingCount = d.pendingAssignmentCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI cards (clickable) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
        }}
      >
        <KpiCard
          to="/manufacturing/assignments"
          title="배정 대기 계약"
          value={pendingCount}
          hint={
            pendingCount > 0
              ? '배정을 기다리는 계약이 있습니다. 누르면 배정 인박스가 열립니다.'
              : '누르면 배정 인박스가 열립니다.'
          }
          warningLevel="amber"
        />
        <KpiCard
          to="/manufacturing/production-plan"
          title="이번 주 완성 예정"
          value={d.plansThisWeek.length}
          hint={`계획 ${d.plansThisWeek.length}건입니다. 누르면 제조 계획이 열립니다.`}
        />
        <KpiCard
          to="/manufacturing/production-plan"
          title="30일 내 완성 예정"
          value={d.plansThisMonthCount}
          hint="계획 수입니다. 누르면 제조 계획이 열립니다."
        />
      </div>

      {/*
       * 수정 사항2.pdf p.1 — 카드 폭 정리.
       * 2 개 bar chart 가 sparse 한데 full-width 로 늘어나 가독성 ↓.
       * auto-fit 2-col grid (minmax 340px) — 좁은 화면에선 자동으로
       * 1-col 으로 wrap.
       */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 16,
        }}
      >
        {/* 로봇 상태 분포 — bar chart */}
        <Section title="로봇 상태 분포">
          <HorizontalBars
            items={d.robotsByStatus.map((r) => ({
              key: r.status,
              label: ROBOT_STATUS_LABEL[r.status] ?? r.status,
              count: r.count,
              color: ROBOT_STATUS_COLOR[r.status],
            }))}
            emptyText="로봇 데이터가 없습니다."
          />
        </Section>

        {/* 제조 계획 상태 — bar chart */}
        <Section title="제조 계획 상태">
          <HorizontalBars
            items={d.productionByStatus.map((p) => ({
              key: p.status,
              label: PLAN_STATUS_LABEL[p.status] ?? p.status,
              count: p.planCount,
              value: `${p.plannedRobotCount}대`,
              color: PLAN_STATUS_COLOR[p.status],
            }))}
            emptyText="제조 계획 데이터가 없습니다."
          />
        </Section>
      </div>

      {/*
       * S2 Phase 2.10 (시트1) — 제조 실적 KPI.
       * 월별 생산량(firstProductionYm)/출고량(firstShipmentYm) 추세 +
       * 모델별 보유 비중. 차트 라이브러리 없이 경량 bar 로 렌더.
       */}
      {kpi.isLoading ? (
        <div style={SHARED_CARD}>
          <div style={SHARED_CARD_TITLE}>월별 제조 실적</div>
          <div style={SHARED_EMPTY_HINT}>불러오는 중…</div>
        </div>
      ) : kpi.error ? (
        <div style={SHARED_CARD}>
          <div style={SHARED_CARD_TITLE}>월별 제조 실적</div>
          <div style={SHARED_EMPTY_HINT}>제조 실적을 불러오지 못했습니다.</div>
        </div>
      ) : (
        kpi.data && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 16,
            }}
          >
            <Section title={`월별 생산량, 누적 ${kpi.data.producedTotal}대`}>
              <MonthlyTrendBars
                points={kpi.data.production}
                color="var(--warm-400)"
                emptyText="생산 데이터가 없습니다."
                formatValue={(n) => `${n}대`}
              />
            </Section>
            <Section title={`월별 출고량, 누적 ${kpi.data.shippedTotal}대`}>
              <MonthlyTrendBars
                points={kpi.data.shipment}
                color="var(--robot-shipping)"
                emptyText="출고 데이터가 없습니다."
                formatValue={(n) => `${n}대`}
              />
            </Section>
            <Section title="모델별 비중">
              <HorizontalBars
                items={kpi.data.byModel.map((m) => ({
                  key: m.modelCode,
                  label: m.modelDisplayName,
                  count: m.count,
                  value: `${m.count}대`,
                  color: 'var(--robot-operating)',
                }))}
                emptyText="모델 데이터가 없습니다."
              />
            </Section>
          </div>
        )
      )}

      {/*
       * S3 Phase 3.8 (spec §3.9) — 제조 대시보드 전용 캘린더.
       * 제조 계획 / 출고 / 로봇 교체 / 회수 4 이벤트 소스 + 토글.
       * 교체 카드에서 출고 대기(READY_TO_SHIP) 로봇을 교체 SO 에 할당.
       */}
      <ManufacturingCalendar />

      {/* 이번 주 완성 예정 — detail list */}
      {d.plansThisWeek.length > 0 && (
        <Section title="이번 주 완성 예정 상세">
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 0' }}>계획</th>
                <th style={{ textAlign: 'left' }}>모델</th>
                <th style={{ textAlign: 'right' }}>수량</th>
                <th style={{ textAlign: 'right' }}>완성 예정</th>
                <th style={{ textAlign: 'right' }}>진행률</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {d.plansThisWeek.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td
                    style={{
                      padding: '6px 0',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                    }}
                  >
                    {p.humanId}
                  </td>
                  <td>{p.model.displayName}</td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {p.plannedCount}
                  </td>
                  {/* 추가수정 2차(26-07-27) 정정 — 제조 계획 목록(ProductionPlanPage)
                      과 같은 완성 예정일. 결정 ②A 대로 KST 날짜 표기로 통일. */}
                  <td
                    style={{
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {kstYmd(p.estimatedCompletionAt) ?? '–'}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {p.progressPercent}%
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Link to="/manufacturing/production-plan" style={linkSmall}>
                      상세 →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* DELAYED 알림 — 명시적 강조 (danger 톤) */}
      {d.delayedPlans.length > 0 && (
        <div
          style={{
            ...SHARED_CARD,
            background: 'var(--bg-danger-subtle)',
            borderColor: 'var(--border-danger-saturated)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div
            style={{
              ...SHARED_CARD_TITLE,
              color: 'var(--fg-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="siren" size={16} color="var(--fg-danger)" /> 지연된 계획
          </div>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 0' }}>계획</th>
                <th style={{ textAlign: 'left' }}>모델</th>
                <th style={{ textAlign: 'right' }}>수량</th>
                <th style={{ textAlign: 'right' }}>지연된 예정일</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {d.delayedPlans.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td
                    style={{
                      padding: '6px 0',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                    }}
                  >
                    {p.humanId}
                  </td>
                  <td>{p.model.code}</td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {p.plannedCount}대
                  </td>
                  {/* 추가수정 2차(26-07-27) 정정 — 지연 표의 완성 예정일도 같은 표기. */}
                  <td
                    style={{
                      textAlign: 'right',
                      color: 'var(--fg-danger)',
                      fontFamily: 'var(--font-mono)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {kstYmd(p.estimatedCompletionAt) ?? '–'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Link to="/manufacturing/production-plan" style={linkSmall}>
                      처리 →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state — DELAYED + thisWeek 둘 다 0 일 때 */}
      {d.plansThisWeek.length === 0 && d.delayedPlans.length === 0 && (
        <div style={SHARED_CARD}>
          <div style={SHARED_CARD_TITLE}>제조 활동</div>
          <div style={SHARED_EMPTY_HINT}>이번 주 완성 예정이나 지연된 계획이 없습니다.</div>
        </div>
      )}

      <AutoRefreshHint isFetching={q.isFetching} />
    </div>
  );
}

const linkSmall: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};

// round10 B3 — MonthlyTrendBars 는 _shared.tsx 로 이동(제조·사업 대시보드 공용).
